import { Fragment, PropsWithChildren, useEffect, useState } from 'react';
import { useStyles } from './ReceiptScreen.styles';
import { useGlobalDispatch, useGlobalState, TDispatch } from '../../common/context/AppContext';
import { InsuranceBox, formatPhone } from '@flos/react-components';
import {
    BUSINESS_LINES,
    EMAIL_EVENT,
    EMAIL_IDENTIFIER,
    ICoverage,
    IEmailPayloadBuy,
    IEmailPayloadQuote,
} from '../../common/interface/IPayload';
import {
    IAppProviderState,
    IAppProps,
    REDUCER_KEY,
    FLOW,
    YES_NO,
    ICommonEmailPayload,
} from '../../common/interface/IAppProvider';
import GridWrapper from '../../components/shared/grid-wrapper/GridWrapper';
import RenderHtml from '../../components/shared/render-html/RenderHtml';
import { baseStyles } from '../../theme/base.styles';
import { dateStr, vanInsuranceValue } from '../../common/utils/helpers/GenericHelpers';
import { sendEmail } from '../../common/async/AsyncCalls';
import { ecommerce, track, errorTypes } from 'c9-tracking';
import * as helpers from './ReceiptScreenHelpers';
import craftmanCoveragesConfig from '../../assets/json/craftmanCoveragesConfig.json';
import { WhitelabelPartnerLabel } from '../../common/interface/IAppConfig';

interface IState {
    loading: boolean;
}

export default function ReceiptScreenView(props: PropsWithChildren<IAppProps>) {
    const hippoTexts = props.config.resourceBundles['onsa-erhvervsforsikring/'].read();
    const common = props.config.resourceBundles['dk.onsa-common'].read();
    const cb_rb = props.config.contentBlocks['receipt-buy'].read();
    const cb_rq = props.config.contentBlocks['receipt-quote'].read();
    const cb_hrb = props.config.contentBlocks['common/help-receipt-b2b'].read();
    const globalStates = useGlobalState();
    const { salesComplete, product, contactInfo, quote, isLoading, cvrData, inception, flow } = useGlobalState();
    const appDispatch = useGlobalDispatch();
    const [state, setState] = useState<IState>({ loading: false });
    const baseClasses = baseStyles();
    const classes = useStyles();
    const markers = [
        { key: '{{phone}}', value: formatPhone(contactInfo.phone || quote.phone) },
        { key: '{{date}}', value: dateStr(inception.date) },
    ];

    const price = { amount: product.monthlyPrice, type: '', unit: common['unit.month'] };

    useEffect(() => {
        if (flow === FLOW.BUY && !state.loading && !salesComplete && product) {
            setState((prevState) => ({ ...prevState, loading: true }));
            emailBuyFlow(globalStates, props, appDispatch);
        } else if (flow === FLOW.QUOTE && !state.loading && !salesComplete) {
            setState((prevState) => ({ ...prevState, loading: true }));
            emailQuoteFlow(globalStates, props, appDispatch);
        }
    }, [globalStates, contactInfo, flow, product, props, salesComplete, state.loading, appDispatch]);

    if (!isLoading && salesComplete) {
        return (
            <div className={classes.wrapper}>
                <GridWrapper showChatIcon={false}>
                    <RenderHtml substitutes={markers}>{flow === FLOW.BUY ? cb_rb.data : cb_rq.data}</RenderHtml>
                    {flow === FLOW.BUY && <h3>{hippoTexts['receipt-insurance-box-header']}</h3>}

                    <InsuranceBox
                        title={`Erhvervsforsikring`}
                        iconshape="factory"
                        iconsize="42"
                        textline1={cvrData.name}
                        price={flow === FLOW.BUY ? price.amount.toString() : ''}
                        priceformula={flow === FLOW.BUY ? price.unit : ''}
                        className={classes.insuranceBox}
                    />

                    <div className={`${classes.footer} ${baseClasses.spacingMedium}`}>
                        <RenderHtml>{cb_hrb.data}</RenderHtml>
                    </div>
                </GridWrapper>
            </div>
        );
    }

    return <Fragment />;
}

/*
 * Common data for both buy and quote flow
 */
const common = (states: IAppProviderState) => {
    const { contactInfo, quote, cvrData, selectedPackage, partnerDiscount, partner, hasManyOwner, cpr } = states;
    let commonPayload: ICommonEmailPayload = {
        emailAddress: contactInfo.email || quote.email,
        fullName: contactInfo.name || quote.name,
        phoneNumber: contactInfo.phone?.replace(/\s/g, '') || quote.phone.replace(/\s/g, ''),
        companyName: cvrData.name,
        cvrNumber: `${cvrData.cvrNumber}`,
        industryCode: cvrData.business.key,
        industryName: cvrData.business.name,
        numberOfEmployees: Math.ceil(+cvrData.employees),
        packageName: selectedPackage?.packageName ?? 'BASISFORSIKRING',
        partnerDiscount: partnerDiscount.percent ? `${partnerDiscount.percent}%` : '',
        partnerName: partner ? WhitelabelPartnerLabel[partner] : '',
    };
    if (hasManyOwner !== undefined) {
        commonPayload = {
            ...commonPayload,
            manyOwners: hasManyOwner === 'Ja' ? 'Flere' : '1',
        };
    }
    if (cpr !== '') {
        commonPayload = {
            ...commonPayload,
            cpr: cpr,
        };
    }
    return commonPayload;
};

/*
 * Handling email for buy flow
 */
const emailBuyFlow = (states: IAppProviderState, props: PropsWithChildren<IAppProps>, appDispatch: TDispatch) => {
    const {
        inception,
        claimHistory,
        product,
        cvrData,
        contactInfo,
        lockinPeriod,
        bankInfo,
        eCommerceProduct,
        turnover,
        attributes,
        selectedPackage,
        partnerDiscount,
        partner,
        isHideOccupationalInjury,
    } = states;

    const coveragesList = () => {
        if (cvrData.businessLine === BUSINESS_LINES.OFFICE_WORK) {
            let coverages = attributes.coverages;
            if (isHideOccupationalInjury) {
                coverages = coverages.filter((coverage: ICoverage) => !coverage.name.includes('Lovpligtig'));
            }
            return coverages;
        } else {
            return selectedPackage.coverages.filter((coverage: ICoverage) => coverage.selected === true);
        }
    };

    const coverages = helpers.getCoverages(coveragesList());
    const coverageAmounts =
        cvrData.businessLine === BUSINESS_LINES.OFFICE_WORK
            ? helpers.getCoverageAmounts(attributes.coverageAmounts, Math.ceil(+cvrData.employees))
            : craftmanCoveragesConfig.coverageAmounts
                  .find((covAmount) => covAmount.coveragePackageName === selectedPackage.packageName)
                  ?.items.map((item: any) => {
                      return { name: item.name, coverageAmount: item.valueInt };
                  });
    const additionalCoverages = helpers.getAdditionalCoverages(product.chosenCoverages, turnover);

    const salesCompletedPayload: IEmailPayloadBuy = {
        identifier: EMAIL_IDENTIFIER.SALES_COMPLETE,
        event: EMAIL_EVENT.BUY,
        existingBusinessInsurances: inception.currently === YES_NO.YES,
        claims: claimHistory.claimHistory === 'Ja',
        numberOfClaims: claimHistory.noOfClaims ? +claimHistory.noOfClaims : 0,
        effectiveDate: inception.date
            ? new Intl.DateTimeFormat('en-GB').format(new Date(inception.date)).replace(/\//g, '-')
            : '',
        monthlyPrice: product.monthlyPrice,
        chargingAddress: `${cvrData.address}, ${cvrData.zipcodeAndCity}`,
        insuranceAddress: contactInfo.insuranceAddress?.address || `${cvrData.address}, ${cvrData.zipcodeAndCity}`,
        agreementDuration: lockinPeriod,
        bankRegistrationNumber: bankInfo.regNumber,
        bankAccountNumber: bankInfo.accNumber,
        coverages: coverages,
        coverageAmounts: coverageAmounts || [],
        additionalCoverages: additionalCoverages,
        vanCallMe: product.van ? 'J' : 'N',
        yearlyTurnover: turnover,
        ...common(states),
    };

    props.c9log.debug(
        `email: ${salesCompletedPayload.emailAddress}, cvr: ${salesCompletedPayload.cvrNumber}`,
        'Email-Buy',
    );

    sendEmail(props, appDispatch, salesCompletedPayload)
        .then(() => {
            ecommerce.purchase([eCommerceProduct], '');
            track.interact(`${cvrData.businessLine}: Buy email sent`);
            track.convert([
                { key: 'identifier', value: EMAIL_IDENTIFIER.SALES_COMPLETE },
                { key: 'event', value: EMAIL_EVENT.BUY },
                { key: 'cvr-number', value: cvrData.cvrNumber },
                { key: 'business line', value: cvrData.businessLine },
                { key: 'monthly-price', value: product.monthlyPrice },
                {
                    key: 'van',
                    value: vanInsuranceValue(cvrData.businessLine, product.van),
                },
                {
                    key: 'partner',
                    value: partner ?? 'N/A',
                },
                {
                    key: 'discount',
                    value: partnerDiscount.percent ? `${partnerDiscount.percent} %` : 'N/A',
                },
                {
                    key: 'additionalCoverages',
                    value: additionalCoverages
                        .filter((item) => item.name && item.selected)
                        .map((item) => item.name)
                        .join(', ')
                        .toString(),
                },
            ]);
            appDispatch({ type: REDUCER_KEY.SET_SALES_COMPLETE, payload: true });
            appDispatch({ type: REDUCER_KEY.SET_STATE_CHANGE, payload: true });
        })
        .catch((err) => {
            props.c9log.error(`Buy endpoint sending e-mail failed, error: ${err}`, 'ReceiptScreen');
            track.fault(`Buy endpoint sending e-mail failed. ${err}`, errorTypes.tech, 'ReceiptScreen');
        });
};

/*
 * Handling email for quote flow
 */
const emailQuoteFlow = (states: IAppProviderState, props: PropsWithChildren<IAppProps>, appDispatch: TDispatch) => {
    const { quote, cvrData } = states;
    const quotePayload: IEmailPayloadQuote = {
        identifier: EMAIL_IDENTIFIER.ORDER_QUOTE,
        event: quote.condition,
        description: quote.remarks ?? '',
        ...common(states),
    };

    props.c9log.debug(
        `Quote condition: ${quote.condition}, email: ${quotePayload.emailAddress}, cvr: ${quotePayload.cvrNumber}`,
        'Email-quote',
    );

    sendEmail(props, appDispatch, quotePayload)
        .then(() => {
            track.interact(`${cvrData.businessLine}: Quote email sent`);
            track.convert([
                { key: 'identifier', value: EMAIL_IDENTIFIER.ORDER_QUOTE },
                { key: 'event', value: quote.condition },
                { key: 'cvrNumber', value: cvrData.cvrNumber },
                { key: 'business line', value: cvrData.businessLine },
            ]);
            appDispatch({ type: REDUCER_KEY.SET_SALES_COMPLETE, payload: true });
            appDispatch({ type: REDUCER_KEY.SET_STATE_CHANGE, payload: true });
        })
        .catch((err) => {
            props.c9log.error(`Quote endpoint ${quote.condition} failed. error: ${err}`, 'ReceiptScreen');
            track.fault(`Quote endpoint ${quote.condition} failed. ${err}`, errorTypes.tech, 'ReceiptScreen');
        });
};
